import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase, supabaseReady } from '@/lib/supabase'
import { exerciseKey } from '@/lib/exerciseKey'

/** UUID check — sample/demo athlete ids ('usa1','1') are not persisted. */
export const isRealAthleteId = (id: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)

export interface SetLogRow {
  week: number | null
  day: number | null
  set_index: number
  weight: number | null
  reps: number | null
  logged_at: string
  program_id: string | null
  exercise_id: string
  exercise_name: string | null
  /** Derived lift identity — see lib/exerciseKey. */
  key: string
}

export interface ExerciseHistory {
  sessions: { week: number | null; best: number; date: string; weights: number[]; programId: string | null }[]
  pr: number | null
  lastWeight: number | null
  totalSets: number
}

/**
 * Every weighted set an athlete has ever logged, across all programs. Loaded
 * once per athlete (the busiest athlete has ~140 rows) and shared by every
 * exercise card, so history no longer depends on which program is active.
 */
export function useAthleteLogs(athleteId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['athlete-logs', athleteId],
    enabled: enabled && supabaseReady && isRealAthleteId(athleteId),
    queryFn: async (): Promise<SetLogRow[]> => {
      const { data, error } = await supabase
        .from('set_logs')
        .select('week,day,set_index,weight,reps,logged_at,program_id,exercise_id,exercise_name')
        .eq('athlete_id', athleteId)
        .not('weight', 'is', null)
        .order('logged_at', { ascending: true })
      if (error) throw error
      return (data ?? []).map((r) => ({
        ...(r as Omit<SetLogRow, 'key'>),
        key: exerciseKey((r as { exercise_name: string | null }).exercise_name) || (r as { exercise_id: string }).exercise_id,
      }))
    },
  })
}

/** Turn one lift's rows into a chronological session history. */
function buildHistory(rows: SetLogRow[]): ExerciseHistory {
  // A session is a program + week + day. Grouping by week alone merged "Week 1"
  // of an old program with "Week 1" of the new one, since weeks restart at 1.
  const byKey = new Map<string, { week: number | null; date: string; weights: number[]; programId: string | null }>()
  for (const r of rows) {
    if (r.weight == null) continue
    const key = r.week != null
      ? `${r.program_id ?? '-'}|${r.week}|${r.day ?? '-'}`
      : r.logged_at.slice(0, 10)
    const g = byKey.get(key) ?? { week: r.week, date: r.logged_at, weights: [], programId: r.program_id }
    g.weights.push(r.weight)
    if (r.logged_at > g.date) g.date = r.logged_at
    byKey.set(key, g)
  }
  const sessions = [...byKey.values()]
    .map((g) => ({ ...g, best: Math.max(...g.weights) }))
    .sort((a, b) => a.date.localeCompare(b.date))

  const all = rows.map((r) => r.weight).filter((w): w is number => w != null)
  return {
    sessions,
    pr: all.length ? Math.max(...all) : null,
    lastWeight: sessions.length ? sessions[sessions.length - 1].best : null,
    totalSets: all.length,
  }
}

/**
 * One lift's history for an athlete, matched by lift NAME across every program
 * they've ever had — so changing or re-importing a program keeps their history.
 */
export function useExerciseHistory(athleteId: string, exerciseName: string, enabled: boolean) {
  const logs = useAthleteLogs(athleteId, enabled)
  const key = exerciseKey(exerciseName)
  const data = useMemo(
    () => (logs.data && key ? buildHistory(logs.data.filter((r) => r.key === key)) : undefined),
    [logs.data, key]
  )
  return { data, isLoading: logs.isLoading, isError: logs.isError }
}

export interface SaveSetInput {
  athleteId: string
  programId?: string | null
  exerciseId: string
  exerciseName: string
  week?: number | null
  day?: number | null
  setIndex: number
  weight: number | null
  reps?: number | null
}

/** Upsert a single logged set (idempotent per athlete/program/week/day/exercise/set). */
export function useSaveSet() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: SaveSetInput) => {
      if (!supabaseReady || !isRealAthleteId(input.athleteId)) return
      const row = {
        athlete_id: input.athleteId,
        program_id: input.programId ?? null,
        exercise_id: input.exerciseId,
        exercise_name: input.exerciseName,
        week: input.week ?? null,
        day: input.day ?? null,
        set_index: input.setIndex,
        weight: input.weight,
        reps: input.reps ?? null,
      }
      const { error } = await supabase
        .from('set_logs')
        .upsert(row, { onConflict: 'athlete_id,program_id,week,day,exercise_id,set_index' })
      if (error) throw error
    },
    onSuccess: (_d, input) => {
      qc.invalidateQueries({ queryKey: ['athlete-logs', input.athleteId] })
      qc.invalidateQueries({ queryKey: ['athlete-report', input.athleteId] })
      qc.invalidateQueries({ queryKey: ['session-sets', input.athleteId] })
    },
  })
}

/**
 * The weights already logged for this exact session, keyed by set index, used
 * to pre-fill the inputs. Scoped to the program as well as week/day: without it
 * a new program's Week 1 Day 1 pre-filled with the old program's numbers.
 */
export function useSessionSets(
  athleteId: string,
  exerciseId: string,
  programId: string | null | undefined,
  week: number | null,
  day: number | null,
  enabled: boolean
) {
  const scoped = Boolean(programId && isRealAthleteId(programId))
  return useQuery({
    queryKey: ['session-sets', athleteId, exerciseId, scoped ? programId : null, week, day],
    enabled: enabled && supabaseReady && isRealAthleteId(athleteId) && Boolean(exerciseId),
    queryFn: async (): Promise<Record<number, string>> => {
      let q = supabase
        .from('set_logs')
        .select('set_index, weight')
        .eq('athlete_id', athleteId)
        .eq('exercise_id', exerciseId)
      if (scoped) q = q.eq('program_id', programId!)
      q = week == null ? q.is('week', null) : q.eq('week', week)
      q = day == null ? q.is('day', null) : q.eq('day', day)
      const { data, error } = await q
      if (error) throw error
      const map: Record<number, string> = {}
      for (const r of data ?? []) {
        if (r.weight != null) map[r.set_index as number] = String(r.weight)
      }
      return map
    },
  })
}
